import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  useWindowDimensions,
  Platform,
  StyleSheet,
  Image,
} from "react-native";
import { CommonActions, useNavigation } from "@react-navigation/native";
import { LinearGradient } from "expo-linear-gradient";
import { SvgUri } from "react-native-svg";

import AppBackground from "../../../components/AppBackground";
import { useThemeSettings } from "../../../ThemeProvider";
import getGlobalStyles from "../../../globalStyles";
import { APP_CONFIG_KEY, getConfig, updateUser } from "../../../services/apiService";
import { ROUTES } from "../../../shared/constants/routes";
import { TOUR_TYPES, useTourStore } from "../../../store/useTourStore";
import { useNavStore } from "../../../store/navStore";
import { isSingleWord } from "../../../shared/helpers/utils";
import { TrackedTouchable } from "../../../analytics/components";

const WelcomeScreen = () => {
  const { theme } = useThemeSettings();
  const gStyles = getGlobalStyles(theme);
  const { width, height } = useWindowDimensions();
  const navigation = useNavigation<any>();
  const hasSeenOnboarding = useTourStore((s) =>
    s.hasSeen(TOUR_TYPES.ONBOARDING_FLOW),
  );
  const setSeen = useTourStore((s) => s.setSeen);

  const BUBBLE_COLORS = [
    theme.colors.bubbleColor1,
    theme.colors.bubbleColor2,
    theme.colors.bubbleColor3,
  ];

  const [selected, setSelected] = useState<string[]>([]);
  const [interestList, setInterestList] = useState<any[]>([]);
  const [isCompleting, setIsCompleting] = useState(false);

  useEffect(() => {
    const loadInterests = async () => {
      try {
        const data = await getConfig(APP_CONFIG_KEY.ONBOARDING_INTERESTS);
        setInterestList(data || []);
      } catch (error) {
        console.error("Failed to fetch onboarding interests", error);
      }
    };
    loadInterests();
  }, []);

  useEffect(() => {
    if (!hasSeenOnboarding || isCompleting) return;
    useNavStore.getState().setHistory([{ name: ROUTES.HOME }]);
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: ROUTES.HOME, params: { selectedTab: ROUTES.READ } }],
      }),
    );
  }, [hasSeenOnboarding, isCompleting, navigation]);

  /* ---------------- RESPONSIVE LAYOUT FLAGS ---------------- */

  const isSmall = width < 480;
  const isMedium = width >= 480 && width < 900;

  /* ---------------- RESPONSIVE CHIP SIZE ---------------- */

  const getChipSize = () => {
    if (width >= 1200) return 100;
    if (width >= 900) return 95;
    if (width >= 600) return 82;
    return 72;
  };

  const chipSize = getChipSize();

  const getBubbleTitleFontSize = () => {
    if (width >= 1200) return 14;
    if (width >= 900) return 13;
    if (width >= 600) return 12;
    return 11;
  };

  const bubbleTitleFontSize = getBubbleTitleFontSize();

  /* ---------------- RESPONSIVE COLUMNS ---------------- */

  const numColumns = useMemo(() => {
    if (width >= 1200) return 6;
    if (width >= 900) return 5;
    if (width >= 600) return 4;
    return 3;
  }, [width]);

  /* ---------------- TOGGLE ---------------- */

  const toggleTopic = (tag: string) => {
    setSelected((prev) =>
      prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag],
    );
  };

  const completeOnboardingAndGoHome = async (skip = false) => {
    setIsCompleting(true);
    const selectedItems = skip
      ? []
      : interestList
          .filter((i) => selected.includes(i?.label))
          .map((item) => ({
            tags: item?.tags,
            label: item?.label,
            collectionName: item?.collectionName,
          }));

    try {
      if (selectedItems.length > 0) {
        await updateUser(selectedItems);
      }
    } catch (error) {
      console.error("Failed to update user interests", error);
    } finally {
      setSeen(TOUR_TYPES.ONBOARDING_FLOW);
      useNavStore.getState().setHistory([
        { name: ROUTES.HOME, params: { selectedTab: ROUTES.READ } },
      ]);
      navigation.dispatch(
        CommonActions.reset({
          index: 0,
          routes: [{ name: ROUTES.HOME, params: { selectedTab: ROUTES.READ } }],
        }),
      );
    }
  };

  /* ---------------- RENDER ITEM ---------------- */
  const renderItem = ({ item, index }: any) => {
    const isActive = selected.includes(item.label);
    const baseColor = BUBBLE_COLORS[index % BUBBLE_COLORS.length];
    const bubbleTextColor = theme.dark
      ? theme.colors.black
      : theme.colors.white;
    const gradientStart = !isActive ? `${baseColor}95` : baseColor;
    const labelLength = item.label?.length ?? 0;
    const isSingle = isSingleWord(item.label);

    const maxLines = isSingle ? 1 : 2;

    let labelFontSize = isActive
      ? bubbleTitleFontSize + 1
      : bubbleTitleFontSize;

    if (isSingle) {
      if (labelLength > 10 && labelLength <= 14) {
        labelFontSize -= 1;
      } else if (labelLength > 14) {
        labelFontSize -= 2;
      }
    } else {
      if (labelLength > 18) {
        labelFontSize -= 1;
      }
    }

    return (
      <TouchableOpacity
        activeOpacity={0.85}
        onPress={() => toggleTopic(item.label)}
        style={[
          styles.chip,
          {
            width: chipSize,
            height: chipSize,
            borderRadius: chipSize / 2,
            transform: [{ scale: isActive ? 1.3 : 1.05 }],
          },
          Platform.OS === "web" && styles.webShadow,
        ]}
      >
        <LinearGradient
          colors={[gradientStart, baseColor]}
          style={[
            styles.bubbleFill,
            {
              width: chipSize,
              height: chipSize,
              borderRadius: chipSize / 2,
              // flex : 1
            },
          ]}
        >
          <Text
            numberOfLines={maxLines}
            style={[
              styles.bubbleLabel,
              gStyles.header3,
              {
                color: bubbleTextColor,
                fontSize:  bubbleTitleFontSize,
                marginBottom: isActive ? 20 : 0,
                fontWeight : "500",
              },
            ]}
          >
            {item.label}
          </Text>
          {isActive && !!item.icon && (
            item.icon.endsWith(".svg") ? (
              <SvgUri
                uri={item.icon}
                width={10}
                height={10}
                color={bubbleTextColor}
                style={[styles.bubbleIcon, { bottom: chipSize * 0.1 }]}
              />
            ) : (
              <Image
                source={{ uri: item.icon }}
                style={[
                  styles.bubbleIcon,
                  {
                    width: chipSize * 0.3,
                    height: chipSize * 0.3,
                    marginTop : chipSize * 0.4
                  },
                ]}
                tintColor={bubbleTextColor}
                resizeMode="contain"
              />
            )
          )}
        </LinearGradient>
      </TouchableOpacity>
    );
  };

  return (
    <AppBackground>
      <View style={styles.screen}>
        {/* HEADER SECTION */}
        <View
          style={[
            styles.headerContainer,
            isSmall && styles.headerContainerSmall,
          ]}
        >
          <View style={styles.headerRow}>
            <View style={{ flex: 1, alignItems: "center" }}>
              <Text
                style={[
                  gStyles.centerMessage,
                  styles.title,
                  isSmall && styles.titleSmall,
                  isMedium && styles.titleMedium,
                ]}
              >
                Follow what shapes your world
              </Text>
            </View>
          </View>
        </View>

        <Text
          style={[
            gStyles.subtitle,
            styles.subtitle,
            isSmall && styles.subtitleSmall,
          ]}
        >
          Tap topics to build your personal collection.
        </Text>

        {/* CARD */}
        <View style={[styles.card, isSmall && styles.cardSmall]}>
          <FlatList
            style={styles.list}
            data={interestList}
            renderItem={renderItem}
            keyExtractor={(item) => item.label}
            numColumns={numColumns}
            key={numColumns}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.gridContainer}
            columnWrapperStyle={numColumns > 1 ? styles.rowWrapper : undefined}
          />

          <TrackedTouchable
            context={{
              flow: "onboarding",
              cta: selected.length === 0 ? "ONBOARDING_SKIP" : "ONBOARDING_COMPLETE",
              source_component:
                selected.length === 0
                  ? "WelcomeOnboardingSkip"
                  : "WelcomeOnboardingComplete",
              selected_interest_count: selected.length,
            }}
            style={[
              gStyles.fullWidthButton,
              gStyles.buttonActiveStyle,
              styles.ctaButton,
              isSmall && styles.ctaButtonSmall,
              selected.length === 0 && { opacity: 0.7 },
            ]}
            onPress={
              () =>
                selected.length === 0
                  ? completeOnboardingAndGoHome(true)
                  : completeOnboardingAndGoHome(false)
            }
          >
            <Text style={[gStyles.buttonActiveLabelStyle, styles.ctaText]}>
              {selected.length > 0
                ? `Save your Collection (${selected.length})`
                : "Skip"}
            </Text>
          </TrackedTouchable>
        </View>
      </View>
    </AppBackground>
  );
};

export default WelcomeScreen;

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    width: "100%",
    alignItems: "center",
    paddingBottom: 20,
  },

  headerRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  headerContainer: {
    width: "100%",
    maxWidth: 1000,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 40,
    marginBottom: 10,
    position: "relative",
    paddingHorizontal: 24,
  },

  headerContainerSmall: {
    paddingHorizontal: 16,
    marginTop: 24,
  },

  skipBtn: {
    position: "absolute",
    right: 0,
    top: 0,
  },

  title: {
    textAlign: "center",
  },

  titleMedium: {
    fontSize: 28,
  },

  titleSmall: {
    fontSize: 18,
  },

  subtitle: {
    textAlign: "center",
    marginBottom: 24,
    maxWidth: 600,
  },

  subtitleSmall: {
    maxWidth: "70%",
  },

  card: {
    width: "100%",
    maxWidth: 1000,
    alignItems: "center",
    flex: 1,
  },

  cardSmall: {
    paddingHorizontal: 16,
  },

  gridContainer: {
    alignItems: "center",
    // paddingVertical: 30,
  },

  list: {
    flex: 1,
    width: "100%",
  },

  rowWrapper: {
    justifyContent: "center",
  },

  chip: {
    justifyContent: "center",
    alignItems: "center",
    margin: 20,
  },

  bubbleFill: {
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
    position: "relative",
  },

  bubbleLabel: {
    fontWeight: "600",
    position: "absolute",
    textAlign: "center",
  },

  bubbleIcon: {},

  webShadow: {
    boxShadow: "0px 8px 18px rgba(0,0,0,0.15)",
  },

  ctaButton: {
    marginTop: 32,
    width: 280,
    borderRadius: 40,
    alignSelf: "center",
  },

  ctaButtonSmall: {
    width: "100%",
  },

  ctaText: {
    fontSize: 16,
    fontWeight: "600",
  },
});